/*
  sections/bracelet-builder.liquid
  Reconciled with Benn's reference package (approved prototype -> real Fisher-Yates
  shuffle -> curated "Let intuition choose" -> perceptual colour-swatch merging).
  See the package's ALGORITHM.md for why the shuffle works the way it does.

  Real changes made for this theme, beyond the reference package:
    - STONES starts from the server-rendered JSON blob (page 1, up to 50
      products -- no API call, no spinner, correct on first paint, same as
      a normal Liquid section). If the source collection has more than 50
      qualifying products, Shopify's Liquid paginate/collection.products
      hard-caps at 50 per page (not configurable) -- any remaining pages
      are fetched in the background via the Section Rendering API
      (?section_id=...&page=N), which re-runs the same Liquid snippet
      server-side for each page. See fetchRemainingPages() below.
    - Sold-out is checked per chosen SIZE, not just per product -- the reference
      package explicitly left this open. Position-based variant resolution
      (never label text) is what makes that possible.
    - CTA does a real POST /cart/add.js, then opens this theme's own cart
      drawer (".site-header__cart-toggle"), and also fires "trb:added" so
      anything else on the page (analytics, etc.) can hook it.
    - The reference package's multi-tier discount ladder is NOT used here --
      the live site already applies the real discount at cart/checkout, so
      this only shows a single-threshold estimate (trb-discount-qty/pct).
*/
(function () {
  "use strict";

  var root = document.querySelector("[data-trb-root]");
  if (!root) return;

  var dataEl = root.querySelector("[data-trb-products]");
  var STONES = dataEl ? JSON.parse(dataEl.textContent) : [];
  var TOTAL_PAGES = dataEl ? parseInt(dataEl.dataset.trbTotalPages, 10) || 1 : 1;
  // Liquid's split leaves a stray "" when a product has zero intention refs.
  function normalizeIntentions(list) {
    list.forEach(function (s) { s.intentions = (s.intentions || []).filter(Boolean); });
  }
  normalizeIntentions(STONES);

  var COLORS = []; // built from STONES below, and again after any background pages arrive

  var MAXSLOTS = parseInt(root.dataset.trbMaxSlots, 10) || 8;
  var MIN_REQUIRED = 3; // first 3 slots are required and can't be removed; 4+ are optional
  var FREE_SHIPPING_MIN = 95; // show the free-shipping pill once the discounted total reaches this
  var DISCOUNT_QTY = parseInt(root.dataset.trbDiscountQty, 10) || 3;
  var DISCOUNT_PCT = (parseFloat(root.dataset.trbDiscountPct) || 0) / 100;
  var SIZES = (root.dataset.trbSizes || "S,M,L").split(",").map(function (s) { return s.trim(); }).filter(Boolean);
  var SLOPE_X = 1, SLOPE_Y = 0.70;

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ---------- colour maths ------------------------------------------------
     sRGB -> CIE Lab, so distances are perceptual rather than arithmetic.
     Everything downstream (which swatches merge, which stones are too
     similar to sit together, how colourful a stone is) is measured here. */
  function toLab(hex) {
    var rgb = [1, 3, 5].map(function (i) { return parseInt(hex.substr(i, 2), 16) / 255; });
    var f = function (c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    var r = f(rgb[0]), g = f(rgb[1]), b = f(rgb[2]);
    var X = r * .4124 + g * .3576 + b * .1805, Y = r * .2126 + g * .7152 + b * .0722, Z = r * .0193 + g * .1192 + b * .9505;
    var h = function (t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; };
    var fx = h(X / .95047), fy = h(Y / 1.0), fz = h(Z / 1.08883);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }
  function deltaE(a, b) {
    var A = toLab(a), B = toLab(b);
    return Math.hypot(A[0] - B[0], A[1] - B[1], A[2] - B[2]);
  }
  function meanHex(list) {
    var t = [0, 0, 0];
    list.forEach(function (h) { [1, 3, 5].forEach(function (p, i) { t[i] += parseInt(h.substr(p, 2), 16); }); });
    return "#" + t.map(function (v) { return Math.round(v / list.length).toString(16).padStart(2, "0"); }).join("");
  }

  /* Colour filter groups, merged by perceptual distance. Grouping by tag alone
     produces dots a person can't tell apart (pink vs purple measured deltaE
     6.0, turquoise vs white 6.6 -- under ~12 is indistinguishable at 26px).
     Merge, and keep every name so "silver" stays findable. Recomputed from
     whatever is on the page, so it self-adjusts as the catalogue grows.
     Both of these run once STONES is populated -- see computeColorMath(),
     called from the boot sequence at the bottom once the fetch resolves. */
  var MERGE_DE = 8;
  var COLOR_ORDER = ["white", "pink", "red", "orange", "gold", "yellow", "green", "turquoise",
    "blue", "purple", "brown", "gray", "grey", "silver", "black", "clear", "other", "rainbow"];

  function buildColorGroups() {
    var buckets = {};
    STONES.forEach(function (s) { (buckets[s.color] = buckets[s.color] || []).push(s.hex || "#CCCCCC"); });
    var groups = Object.keys(buckets)
      .sort(function (a, b) { return (COLOR_ORDER.indexOf(a) + 99) % 199 - (COLOR_ORDER.indexOf(b) + 99) % 199; })
      .map(function (k) { return { names: [k], hexes: buckets[k] }; });

    for (;;) {
      var best = null;
      for (var i = 0; i < groups.length; i++) {
        for (var j = i + 1; j < groups.length; j++) {
          var d = deltaE(meanHex(groups[i].hexes), meanHex(groups[j].hexes));
          if (!best || d < best.d) best = { d: d, i: i, j: j };
        }
      }
      if (!best || best.d >= MERGE_DE) break;
      groups[best.i].names.push.apply(groups[best.i].names, groups[best.j].names);
      groups[best.i].hexes.push.apply(groups[best.i].hexes, groups[best.j].hexes);
      groups.splice(best.j, 1);
    }
    return groups.map(function (g) {
      return { key: g.names.join("|"), label: g.names.join(" · "), hex: meanHex(g.hexes), n: g.hexes.length };
    });
  }

  // L* (lightness) and C* (chroma) drive the shuffle's mood rules.
  function computeColorMath() {
    STONES.forEach(function (s) {
      var lab = toLab(s.hex || "#CCCCCC");
      s.L = lab[0]; s.C = Math.hypot(lab[1], lab[2]);
    });
    COLORS = buildColorGroups();
  }

  function colourLabel(key) {
    var g = COLORS.find(function (c) { return c.key === key; });
    return g ? g.label : key;
  }

  /* ---------- fetching remaining pages: Section Rendering API ------------
     Page 1 is already in STONES (rendered synchronously above). If the
     source collection has more than 50 qualifying products, this fetches
     page 2, 3, ... in the background via
     "<this-page-url>?section_id=<id>&page=N", which re-runs
     bracelet-builder-product-json.liquid server-side for that page --
     full metafield access, no Storefront API app/token needed. Each
     response is the section's full rendered HTML; the JSON blob is pulled
     back out of it the same way it was read on first paint. */
  function fetchRemainingPages() {
    if (TOTAL_PAGES <= 1) return;
    var sectionId = root.dataset.trbSectionId;
    if (!sectionId) return;

    var page = 2;
    function next() {
      if (page > TOTAL_PAGES) {
        computeColorMath();
        // ?prefill_id may name a product that only exists on a background
        // page (2+) -- it wasn't in STONES yet when the initial stack was
        // built, so retry now that every page is in. Skipped once the
        // customer has touched the stack themselves, so this never
        // overwrites a real choice with a late-arriving prefill.
        if (prefillId && !userTouchedStack) {
          var lateStone = byId(prefillId);
          if (lateStone && picks[0] !== lateStone) {
            var rebuilt = buildStack(Math.min(MIN_REQUIRED, MAXSLOTS), [lateStone]);
            picks = rebuilt.stones;
            chosenFor = rebuilt.intention;
            render();
          } else if (!lateStone && !picks.length && STONES.length) {
            // prefill_id never matched a real product even once every page
            // loaded -- fall back to a normal curated stack instead of
            // leaving the stage empty.
            var fallback = buildStack(Math.min(MIN_REQUIRED, MAXSLOTS));
            picks = fallback.stones;
            chosenFor = fallback.intention;
            render();
          }
        }
        var sheet = root.querySelector("#trb-sheet");
        if (sheet && sheet.classList.contains("trb-on")) { renderFilters(); renderGrid(); }
        preloadStoneImages();
        return;
      }
      var url = window.location.pathname + "?section_id=" + encodeURIComponent(sectionId) + "&page=" + page;
      fetch(url, { credentials: "same-origin" })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var frag = document.createElement("div");
          frag.innerHTML = html;
          var el = frag.querySelector("[data-trb-products]");
          if (el) {
            var pageStones = JSON.parse(el.textContent);
            normalizeIntentions(pageStones);
            STONES = STONES.concat(pageStones);
          }
        })
        .catch(function (err) {
          console.error("[bracelet-builder] failed to load page " + page, err);
        })
        .then(function () { page++; next(); });
    }
    next();
  }

  var byId = function (id) { return STONES.find(function (s) { return String(s.id) === String(id); }); };
  var money = function (v) { return "$" + (Math.round(v * 100) / 100).toFixed(2).replace(/\.00$/, ""); };
  var moneyFull = function (v) { return "$" + (Math.round(v * 100) / 100).toFixed(2); };
  var discount = function (n) { return n >= DISCOUNT_QTY ? DISCOUNT_PCT : 0; };

  // Warm the browser's cache for every stone's image (both sizes) so a later
  // shuffle or an opened picker grid doesn't have to wait on network --
  // without this, images pop in one at a time as they download, which reads
  // as choppy on mobile connections.
  function preloadStoneImages() {
    STONES.forEach(function (s) {
      var full = new Image(); full.src = s.img;
      var thumb = new Image(); thumb.src = s.thumb;
    });
  }

  var picks = [];
  var sizeIndex = SIZES.length ? Math.floor((SIZES.length - 1) / 2) : 0;
  var slotIndex = null;
  var fIntent = null, fColour = null;
  var chosenFor = null; // intention behind the current curated stack, shown near the stage
  var userTouchedStack = false; // true once the customer has picked/removed/shuffled -- guards the late prefill re-seed below from clobbering their choices

  function variantForSize(stone, idx) {
    return stone.variants.find(function (v) { return v.position === idx; });
  }
  function isAvailable(stone, idx) {
    var v = variantForSize(stone, idx);
    return !!v && v.available;
  }

  function totals() {
    var gross = picks.reduce(function (a, s) { return a + s.price; }, 0);
    var off = discount(picks.length);
    return { gross: gross, off: off, net: gross * (1 - off), saved: gross * off };
  }

  /* ---------- the stack ----------
     Accepts an optional stones array so the hover-preview below can render
     a "what if" cascade (the real stack with just the hovered slot swapped)
     without mutating picks itself. Defaults to the real stack. */
  function renderStack(stackList) {
    stackList = stackList || picks;
    var rail = root.querySelector("#trb-rail");
    if (!stackList.length) {
      rail.style.width = rail.style.height = "auto";
      rail.innerHTML = '<p class="trb-empty">Your stack will appear here.<br>Add a bracelet to begin.</p>';
      return;
    }
    var stage = root.querySelector(".trb-stage");
    var side = stage.clientWidth || 343;
    var n = stackList.length;

    // Diagonal cascade: each bracelet steps right AND down from the one before.
    // Overlap tightens as the stack grows, so five sit closer together than two
    // do and each bracelet stays big instead of all of them shrinking.
    var t = Math.max(.24, Math.min(.34, .34 - (n - 2) * .02));
    var SX = t * SLOPE_X, SY = t * SLOPE_Y;

    var avail = side - 26;
    var d = Math.min(avail / (1 + (n - 1) * SX), avail / (1 + (n - 1) * SY), side * 0.74);

    var W = d + (n - 1) * d * SX, H = d + (n - 1) * d * SY;
    rail.style.width = W + "px";
    rail.style.height = H + "px";
    rail.innerHTML = stackList.map(function (s, i) {
      return '<img src="' + s.img + '" alt="' + s.title + '" data-i="' + i + '"' +
        ' style="position:absolute;left:' + (i * d * SX).toFixed(1) + 'px;top:' + (i * d * SY).toFixed(1) + 'px;' +
        'width:' + d + 'px;height:' + d + 'px;z-index:' + (40 - i) + ';animation-delay:' + (i * 40) + 'ms">';
    }).join("");

    // The pop-in animation is triggered by JS, not by DOM insertion: a stone
    // that isn't cached yet (a fresh shuffle, a background-fetched page)
    // wasn't done loading by the time the animation would've already played,
    // which looked like no animation at all -- an empty box animating in,
    // then the picture appearing flat once it finally arrived. Waiting for
    // "load" (or "complete" for anything already cached) ties the animation
    // to the moment the image actually has pixels to show.
    rail.querySelectorAll("img").forEach(function (img) {
      if (img.complete) {
        requestAnimationFrame(function () { img.classList.add("trb-loaded"); });
      } else {
        img.addEventListener("load", function () { img.classList.add("trb-loaded"); }, { once: true });
        img.addEventListener("error", function () { img.classList.add("trb-loaded"); }, { once: true });
      }
    });
  }

  /* ---------- stage hover-preview ----------
     While the picker sheet is open, rolling over a product card previews
     the real stack with just the slot being edited swapped to that stone --
     every other slot stays visible, same cascade positions. Deliberately
     does NOT go through renderStack()'s full innerHTML rebuild: that would
     tear down and recreate every <img>, replaying the load/pop-in animation
     on every stone in the stack on every rollover, not just the one being
     previewed. Instead this only repositions the other slots' existing
     <img> elements in place (cheap, no animation) and swaps in a fresh
     element for just the previewed slot (the only one that should animate).
     Purely visual: picks is untouched. */
  var hoverPreviewId = null;

  function setStageSlot(index, stone, n) {
    var rail = root.querySelector("#trb-rail");
    if (!n) {
      rail.style.width = rail.style.height = "auto";
      rail.innerHTML = '<p class="trb-empty">Your stack will appear here.<br>Add a bracelet to begin.</p>';
      return;
    }
    if (!rail.querySelector("img")) rail.innerHTML = ""; // clear the empty-state placeholder, if any

    var stage = root.querySelector(".trb-stage");
    var side = stage.clientWidth || 343;
    var t = Math.max(.24, Math.min(.34, .34 - (n - 2) * .02));
    var SX = t * SLOPE_X, SY = t * SLOPE_Y;
    var avail = side - 26;
    var d = Math.min(avail / (1 + (n - 1) * SX), avail / (1 + (n - 1) * SY), side * 0.74);
    var W = d + (n - 1) * d * SX, H = d + (n - 1) * d * SY;
    rail.style.width = W + "px";
    rail.style.height = H + "px";

    for (var i = 0; i < n; i++) {
      if (i === index) continue;
      var existing = rail.querySelector('img[data-i="' + i + '"]');
      if (existing) {
        existing.style.left = (i * d * SX).toFixed(1) + "px";
        existing.style.top = (i * d * SY).toFixed(1) + "px";
        existing.style.width = d + "px";
        existing.style.height = d + "px";
        existing.style.zIndex = 40 - i;
      }
    }
    // drop anything beyond n (reverting out of an appended preview slot)
    rail.querySelectorAll("img").forEach(function (img) {
      if (+img.dataset.i >= n) img.remove();
    });

    var old = rail.querySelector('img[data-i="' + index + '"]');
    if (old) old.remove();
    if (!stone) return;

    var img = document.createElement("img");
    img.src = stone.img;
    img.alt = stone.title;
    img.dataset.i = index;
    img.style.position = "absolute";
    img.style.left = (index * d * SX).toFixed(1) + "px";
    img.style.top = (index * d * SY).toFixed(1) + "px";
    img.style.width = d + "px";
    img.style.height = d + "px";
    img.style.zIndex = 40 - index;
    rail.appendChild(img);
    if (img.complete) {
      requestAnimationFrame(function () { img.classList.add("trb-loaded"); });
    } else {
      img.addEventListener("load", function () { img.classList.add("trb-loaded"); }, { once: true });
      img.addEventListener("error", function () { img.classList.add("trb-loaded"); }, { once: true });
    }
  }

  function stagePreviewOn(stone) {
    var n = slotIndex >= picks.length ? picks.length + 1 : Math.max(picks.length, 1);
    setStageSlot(slotIndex, stone, n);
  }
  function stagePreviewOff() {
    hoverPreviewId = null;
    var n = picks.length;
    setStageSlot(slotIndex, slotIndex < n ? picks[slotIndex] : null, n);
  }

  /* ---------- slots ----------
     The first MIN_REQUIRED slots always render, filled or not, and never get
     a remove button -- they're required for checkout, not optional adds.
     Slots beyond that are optional: only rendered once filled, plus one
     trailing empty slot to invite adding the next, and removable. */
  function renderSlots() {
    var wrap = root.querySelector("#trb-slots");
    var lastIndex = MIN_REQUIRED - 1;
    if (picks.length >= MIN_REQUIRED && picks.length < MAXSLOTS) lastIndex = picks.length;
    else if (picks.length > lastIndex) lastIndex = picks.length - 1;

    var rows = [];
    for (var i = 0; i <= lastIndex; i++) {
      var s = picks[i];
      if (s) {
        var soldOut = !isAvailable(s, sizeIndex);
        var removable = i >= MIN_REQUIRED;
        rows.push(
          '<div class="trb-slot' + (soldOut ? " trb-slot--soldout" : "") + '" data-i="' + i + '">' +
          '<span class="trb-drag" aria-hidden="true"><svg viewBox="0 0 10 16" fill="currentColor">' +
          '<circle cx="2" cy="2" r="1.4"/><circle cx="8" cy="2" r="1.4"/>' +
          '<circle cx="2" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/>' +
          '<circle cx="2" cy="14" r="1.4"/><circle cx="8" cy="14" r="1.4"/></svg></span>' +
          '<button class="trb-slot-select" type="button" aria-label="Change ' + escapeHtml(s.title) + '">' +
          '<span class="trb-sw" style="background-image:url(\'' + escapeHtml(s.thumb) + '\')"></span>' +
          '<span class="trb-lbl"><span class="trb-v">' + escapeHtml(s.title) + (soldOut ? " · sold out in this size" : "") + '</span>' +
          '<span class="trb-slot-intentions">' + s.intentions.slice(0, 3).map(escapeHtml).join(" · ") + '</span></span></button>' +
          (s.benefits && s.benefits.description && s.benefits.description.trim()
            ? '<button class="trb-benefits-button" data-benefits="' + i + '" type="button" aria-haspopup="dialog" aria-label="Benefits of ' + escapeHtml(s.title) + '"><span aria-hidden="true">ⓘ</span> Benefits</button>'
            : '') +
          '<span class="trb-pr">' + money(s.price) + '</span>' +
          (removable
            ? '<button class="trb-x" data-rm="' + i + '" aria-label="Remove ' + escapeHtml(s.title) + '">&times;</button>'
            : '') +
          '</div>'
        );
      } else {
        rows.push(
          '<div class="trb-slot" data-i="' + i + '" role="button" tabindex="0">' +
          '<span class="trb-drag trb-drag--spacer" aria-hidden="true"></span>' +
          '<span class="trb-sw"></span>' +
          '<span class="trb-lbl"><span class="trb-k">Bracelet ' + (i + 1) + '</span>' +
          '<span class="trb-v trb-ph">Choose your stone</span></span>' +
          '</div>'
        );
      }
    }
    wrap.innerHTML = rows.join("");

    var need = DISCOUNT_QTY - picks.length;
    var btn = root.querySelector("#trb-addmore");
    if (DISCOUNT_PCT > 0 && need > 0) {
      btn.innerHTML = '<span>Add ' + need + ' more bracelet' + (need > 1 ? "s" : "") + '</span>' +
        '<b>Save ' + Math.round(DISCOUNT_PCT * 100) + '% & Free Shipping Orders $95+</b>';
      btn.style.display = "flex";
    } else {
      btn.style.display = "none";
    }
  }

  /* ---------- size ---------- */
  function renderSizes() {
    var wrap = root.querySelector("#trb-sizes");
    wrap.innerHTML = SIZES.map(function (label, i) {
      return '<button class="trb-size" data-i="' + i + '" aria-pressed="' + (i === sizeIndex) + '">' + label + '</button>';
    }).join("");
    renderSizeDetails();
  }

  // Shows the bracelet_sizes metaobject entry (rendered hidden in
  // sections/bracelet-builder.liquid) matching the selected size label,
  // case-insensitive -- same lookup as the PDP's snippets/swatch.liquid.
  function renderSizeDetails() {
    var target = root.querySelector("#trb-size-details");
    if (!target) return;
    var selected = String(SIZES[sizeIndex] || "").toLowerCase().trim();
    var match = Array.prototype.find.call(
      root.querySelectorAll("#trb-size-details-source [data-size]"),
      function (el) { return el.getAttribute("data-size").toLowerCase().trim() === selected; }
    );
    target.innerHTML = match ? match.innerHTML : "";
  }

  /* ---------- price ---------- */
  function renderTotal() {
    var t = totals();
    root.querySelector("#trb-was").textContent = t.off ? moneyFull(t.gross) : "";
    root.querySelector("#trb-now").textContent = picks.length ? money(t.net) : "";

    var blocked = picks.length > 0 && picks.some(function (s) { return !isAvailable(s, sizeIndex); });
    var needMore = MIN_REQUIRED - picks.length;
    var cta = root.querySelector("#trb-cta");
    cta.disabled = needMore > 0 || blocked;

    var pills = "";
    if (t.off) pills += '<span class="trb-pill">You\'re saving ' + money(t.saved) + '</span>';
    if (!cta.disabled && t.net >= FREE_SHIPPING_MIN) pills += '<span class="trb-pill">Free shipping</span>';
    root.querySelector("#trb-saveline").innerHTML = pills;

    if (needMore > 0) {
      cta.textContent = picks.length ? "Choose " + needMore + " more to begin" : "Choose a bracelet to begin";
    } else if (blocked) {
      cta.textContent = "Unavailable in size " + (SIZES[sizeIndex] || "");
    } else {
      cta.textContent = cta.dataset.ctaLabel || "Add set to cart";
    }
  }

  function renderChosenFor() {
    var el = root.querySelector("#trb-chosenfor");
    var show = chosenFor && picks.length;
    el.hidden = !show;
    if (show) el.textContent = "A stack for " + chosenFor;
  }

  function render() { renderStack(); renderSlots(); renderTotal(); renderChosenFor(); }

  /* ---------- picker ---------- */
  function openSheet(i) {
    slotIndex = i; fIntent = null; fColour = null;
    root.querySelector("#trb-sheetTitle").textContent = picks[i] ? "Swap this bracelet" : "Choose bracelet " + (i + 1);
    renderFilters(); renderGrid();
    root.querySelector("#trb-sheet").classList.add("trb-on");
    root.querySelector("#trb-scrim").classList.add("trb-on");
    root.querySelector("#trb-grid").scrollTop = 0;
  }
  function closeSheet() {
    root.querySelector("#trb-sheet").classList.remove("trb-on");
    root.querySelector("#trb-scrim").classList.remove("trb-on");
    if (hoverPreviewId) stagePreviewOff();
  }
  function allIntentions() {
    var c = {};
    STONES.forEach(function (s) { s.intentions.forEach(function (i) { c[i] = (c[i] || 0) + 1; }); });
    return Object.keys(c).sort(function (a, b) { return c[b] - c[a] || a.localeCompare(b); });
  }
  function renderFilters() {
    root.querySelector("#trb-chips").innerHTML =
      '<button class="trb-chip" data-f="" aria-pressed="' + !fIntent + '">All intentions</button>' +
      allIntentions().map(function (i) {
        return '<button class="trb-chip" data-f="' + i + '" aria-pressed="' + (fIntent === i) + '">' + i + '</button>';
      }).join("");

    root.querySelector("#trb-swatches").innerHTML =
      '<button class="trb-chip" data-c="" aria-pressed="' + !fColour + '">All colours</button>' +
      COLORS.map(function (c) {
        return '<button class="trb-swatch" data-c="' + c.key + '" aria-pressed="' + (fColour === c.key) + '"' +
          ' style="background:' + c.hex + '" title="' + c.label + ' (' + c.n + ')"' +
          ' aria-label="' + c.label + ', ' + c.n + ' stones"></button>';
      }).join("");

    updateFades();
  }
  function updateFade(el) {
    var max = el.scrollWidth - el.clientWidth;
    el.classList.toggle("trb-fade-l", el.scrollLeft > 2);
    el.classList.toggle("trb-fade-r", el.scrollLeft < max - 2);
  }
  function updateFades() { root.querySelectorAll(".trb-chips").forEach(updateFade); }

  /* both axes combine with AND: "green" + "grounding" narrows to green grounding stones */
  function matches(s) {
    return (!fIntent || s.intentions.includes(fIntent))
      && (!fColour || fColour.split("|").includes(s.color));
  }

  function renderGrid() {
    var chosen = new Set(picks.filter(function (_, j) { return j !== slotIndex; }).map(function (s) { return s.id; }));
    var list = STONES.filter(matches);

    var active = [fColour ? colourLabel(fColour) : null, fIntent].filter(Boolean).join(" · ");
    root.querySelector("#trb-resultcount").innerHTML =
      "<b>" + list.length + "</b> " + (list.length === 1 ? "stone" : "stones") +
      (active ? ' <span style="opacity:.75">· ' + active + '</span>' : "");
    root.querySelector("#trb-clearfilters").hidden = !(fIntent || fColour);

    var grid = root.querySelector("#trb-grid");
    grid.classList.toggle("trb-isempty", list.length === 0);
    grid.innerHTML = list.length ? list.map(function (s) {
      var dead = s.stock <= 0;
      var unavailableInSize = !dead && !isAvailable(s, sizeIndex);
      var badge = dead ? '<div class="trb-low">Sold out</div>'
        : unavailableInSize ? '<div class="trb-low">Not available in size: ' + (SIZES[sizeIndex] || "") + '</div>'
        : s.stock < 40 ? '<div class="trb-low">Only ' + s.stock + ' left</div>'
        : "";
      return '<button class="trb-card" data-pick="' + s.id + '"' + (dead ? " disabled" : "") + '>' +
        '<img src="' + s.thumb + '" alt="' + s.title + '">' +
        (s.gem_badges || "") + // server-rendered snippets/gem-collection-badges.liquid markup

        '<div class="trb-nm">' + s.title + (chosen.has(s.id) ? " · in stack" : "") + '</div>' +
        '<div class="trb-in">' + s.intentions.join(" · ") + '</div>' +
        '<div class="trb-pp">' + money(s.price) + '</div>' +
        badge +
        '</button>';
    }).join("") : (STONES.length
      ? '<p class="trb-none">Nothing matches both filters.<br>Try clearing one.</p>'
      : '<p class="trb-none">No bracelets available yet.<br>Check back soon.</p>');
  }

  /* ---------- shuffle: Fisher-Yates ----------
     Do NOT use [...arr].sort(() => Math.random() - 0.5): a random comparator
     is not a shuffle, it leaves items near where they started. Measured on
     this catalogue, the first stone came up 22% of the time against a fair
     10%, and the first five supplied 72% of picks instead of 50%. */
  function shuffled(arr) {
    var r = arr.slice();
    for (var i = r.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = r[i]; r[i] = r[j]; r[j] = tmp;
    }
    return r;
  }

  /* ---------- "Let intuition choose" ------------------------------------
     Constrain, then choose freely. Do not score. Measured over 400 stacks,
     pure random produced a near-duplicate pair 24% of the time and two
     clashing loud stones 17% of the time -- the only two failures worth
     stopping. Every soft preference tried (reward separation, reward a
     neutral anchor) made results WORSE: in a small catalogue any preference
     concentrates. Rewarding separation pushed a dark stone into 94% of
     stacks against random's 67%.
     A mood is drawn first and sets the colour budget, so the page shows
     quiet sets and vivid ones rather than one house style. The mood is
     never shown to the customer; the label says the intention only. */
  var LOUD_C = 28, COLOURFUL_C = 18, DUPE_DE = 8;
  var MOODS = [
    { name: "quiet", w: 30, anchor: [0, 15], ceiling: 18, loud: 0, col: [0, 0] },
    { name: "balanced", w: 45, anchor: [18, 28], ceiling: 99, loud: 1, col: [1, 2] },
    { name: "vivid", w: 25, anchor: [28, 99], ceiling: 99, loud: 2, col: [2, 4] }
  ];

  function weightedPick(items, weight) {
    var t = 0; items.forEach(function (i) { t += weight(i); });
    var r = Math.random() * t;
    for (var k = 0; k < items.length; k++) { r -= weight(items[k]); if (r <= 0) return items[k]; }
    return items[items.length - 1];
  }

  function allowedInStack(cand, sel, mood) {
    if (sel.some(function (x) { return deltaE(cand.hex, x.hex) < DUPE_DE; })) return false;
    if (cand.C > mood.ceiling) return false;
    if (cand.C >= LOUD_C && sel.filter(function (x) { return x.C >= LOUD_C; }).length >= mood.loud) return false;
    if (cand.C >= COLOURFUL_C && sel.filter(function (x) { return x.C >= COLOURFUL_C; }).length >= mood.col[1]) return false;
    return true;
  }

  // seed: stones to keep fixed at the front of the result (e.g. the PDP
  // product a customer arrived with via ?prefill_id) -- the rest of the
  // stack is curated around them same as a normal shuffle, but only the
  // newly-picked stones get reshuffled so the seed's position never moves.
  function buildStack(n, seed) {
    // Only curate from stones actually available in the size that's
    // currently selected -- overall stock alone isn't enough, since a stone
    // can be sold out in this size but not others. Falls back in stages so
    // the shuffle never comes back empty.
    var universe = STONES.filter(function (s) { return s.stock > 0 && isAvailable(s, sizeIndex); });
    if (!universe.length) universe = STONES.filter(function (s) { return s.stock > 0; });
    if (!universe.length) universe = STONES;
    if (!universe.length) return { stones: seed ? seed.slice() : [], intention: null };

    var mood = weightedPick(MOODS, function (m) { return m.w; });
    var lo = mood.anchor[0], hi = mood.anchor[1];

    var byIntention = {};
    universe.forEach(function (s) { s.intentions.forEach(function (i) { (byIntention[i] = byIntention[i] || []).push(s); }); });
    var names = Object.keys(byIntention);
    if (!names.length) {
      if (seed && seed.length) {
        var rest = shuffled(universe.filter(function (s) { return seed.indexOf(s) === -1; })).slice(0, n - seed.length);
        return { stones: seed.concat(rest), intention: null };
      }
      return { stones: shuffled(universe).slice(0, n), intention: null };
    }

    var intention = weightedPick(names, function (k) { return byIntention[k].length; });
    var pool = byIntention[intention];
    if (pool.length < n + 2) pool = universe;

    var seedCount = seed ? seed.length : 0;
    var sel;
    if (seed && seed.length) {
      sel = seed.slice();
    } else {
      // widen the SOURCE before ever relaxing the RANGE, so a thin intention
      // cannot quietly turn a quiet stack loud
      var inRange = function (list) { return list.filter(function (s) { return s.C >= lo && s.C <= hi; }); };
      var anchors = inRange(pool).length ? inRange(pool)
        : inRange(universe).length ? inRange(universe)
          : universe.slice().sort(function (a, b) { return Math.abs(a.C - (lo + hi) / 2) - Math.abs(b.C - (lo + hi) / 2); }).slice(0, 5);
      sel = [anchors[Math.floor(Math.random() * anchors.length)]];
    }

    while (sel.length < n) {
      var ok = [];
      [pool, universe].some(function (source) {
        ok = source.filter(function (s) { return sel.indexOf(s) === -1 && allowedInStack(s, sel, mood); });
        return ok.length > 0;
      });
      if (!ok.length) ok = universe.filter(function (s) { return sel.indexOf(s) === -1; });
      if (!ok.length) break;
      // if the mood still owes a colourful stone, spend a remaining slot on one
      var need = mood.col[0] - sel.filter(function (x) { return x.C >= COLOURFUL_C; }).length;
      if (need >= (n - sel.length)) {
        var pref = ok.filter(function (c) { return c.C >= COLOURFUL_C; });
        if (pref.length) ok = pref;
      }
      sel.push(ok[Math.floor(Math.random() * ok.length)]);
    }
    var picked = seedCount ? sel.slice(seedCount) : sel;
    return { stones: seedCount ? sel.slice(0, seedCount).concat(shuffled(picked)) : shuffled(sel), intention: intention };
  }

  /* ---------- add to cart ---------- */
  function addToCart() {
    var cta = root.querySelector("#trb-cta");
    if (cta.disabled) return;

    var items = picks.map(function (s) {
      var variant = variantForSize(s, sizeIndex);
      return variant ? { id: variant.id, quantity: 1, properties: { _bundle_builder_item: "true" } } : null;
    });
    if (items.some(function (i) { return !i; })) {
      showToast("One of these bracelets isn't available in that size.");
      return;
    }

    var setId = "set-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 7);

    cta.disabled = true;
    var prevLabel = cta.textContent;
    cta.textContent = "Adding…";

    fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) { return res.ok ? res.json() : res.json().then(function (err) { throw err; }); })
      .then(function () {
        var count = picks.length;
        document.dispatchEvent(new CustomEvent("trb:added", { detail: { setId: setId, items: items } }));
        // Leave the stack as-is -- added to cart, not reset, so the
        // customer can see what they just added or keep building on it.
        render();
        showToast(count + " bracelet" + (count > 1 ? "s" : "") + " added to your cart.");
        var toggle = document.querySelector(".site-header__cart-toggle");
        if (toggle) toggle.click();
      })
      .catch(function (err) {
        cta.textContent = prevLabel;
        cta.disabled = false;
        showToast((err && err.description) || "Couldn't add that set to your cart.");
      });
  }

  var toastTimer;
  function showToast(msg) {
    var el = root.querySelector("#trb-toast");
    el.textContent = msg;
    el.classList.add("trb-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove("trb-on"); }, 2600);
  }

  /* ---------- slot reorder (drag & drop) ----------
     Pointer Events, not the HTML5 drag-and-drop API: one code path covers
     mouse and touch, and the cursor and "make room" animation stay under our
     own control (native HTML5 DnD hands the drag image/cursor to the browser
     and barely works on touch). Only the 6-dot handle starts a drag -- the
     slot itself is still a plain click target for opening the picker. */
  var slotDrag = null;
  var suppressSlotClick = false;

  function slotDragMove(e) {
    if (!slotDrag || e.pointerId !== slotDrag.pointerId) return;
    var dy = e.clientY - slotDrag.startY;
    if (Math.abs(dy) > 3) slotDrag.moved = true;
    slotDrag.slotEl.style.transform = "translateY(" + dy + "px)";

    var target = slotDrag.startIndex + Math.round(dy / slotDrag.step);
    target = Math.max(0, Math.min(slotDrag.slots.length - 1, target));
    slotDrag.currentIndex = target;

    // Recomputed fully from scratch each move (not accumulated) so reversing
    // direction mid-drag can't leave a sibling's offset out of sync.
    slotDrag.slots.forEach(function (el, idx) {
      if (el === slotDrag.slotEl) return;
      var shift = 0;
      if (slotDrag.startIndex < target && idx > slotDrag.startIndex && idx <= target) shift = -1;
      else if (slotDrag.startIndex > target && idx >= target && idx < slotDrag.startIndex) shift = 1;
      el.style.transform = shift ? "translateY(" + (shift * slotDrag.step) + "px)" : "";
    });
  }

  function slotDragEnd(e) {
    if (!slotDrag || (e && e.pointerId !== slotDrag.pointerId)) return;
    var drag = slotDrag;
    slotDrag = null;
    document.removeEventListener("pointermove", slotDragMove);
    document.removeEventListener("pointerup", slotDragEnd);
    document.removeEventListener("pointercancel", slotDragEnd);
    document.body.classList.remove("trb-dragging-cursor");

    if (drag.moved) {
      // The click that a mouse fires right after this pointerup would otherwise
      // land on whatever slot ended up under the cursor and re-open its picker.
      suppressSlotClick = true;
      setTimeout(function () { suppressSlotClick = false; }, 150);
    }

    if (drag.moved && drag.currentIndex !== drag.startIndex) {
      var moved = picks.splice(drag.startIndex, 1)[0];
      picks.splice(drag.currentIndex, 0, moved);
      userTouchedStack = true;
      chosenFor = null;
      render();
    } else {
      drag.slots.forEach(function (el) { el.style.transform = ""; });
      drag.slotEl.classList.remove("trb-slot--dragging");
    }
  }

  root.querySelector("#trb-slots").addEventListener("pointerdown", function (e) {
    if (e.button !== 0) return;
    var handle = e.target.closest(".trb-drag");
    if (!handle || handle.classList.contains("trb-drag--spacer")) return;
    var slotEl = handle.closest(".trb-slot");
    var wrap = root.querySelector("#trb-slots");
    var slots = Array.prototype.slice.call(wrap.querySelectorAll(".trb-slot")).filter(function (el) {
      return el.querySelector(".trb-drag") && !el.querySelector(".trb-drag--spacer");
    });
    var startIndex = slots.indexOf(slotEl);
    if (!slotEl || startIndex < 0) return;

    e.preventDefault();
    var rect = slotEl.getBoundingClientRect();
    // Gap between two slots' tops, not just one slot's own height, so this
    // stays correct if the row height or its margin ever changes in CSS.
    var step = slots.length > 1
      ? Math.abs(slots[1].getBoundingClientRect().top - slots[0].getBoundingClientRect().top)
      : rect.height + 8;

    slotEl.classList.add("trb-slot--dragging");
    document.body.classList.add("trb-dragging-cursor");

    slotDrag = {
      slotEl: slotEl, slots: slots, startIndex: startIndex, currentIndex: startIndex,
      startY: e.clientY, step: step, pointerId: e.pointerId, moved: false
    };
    document.addEventListener("pointermove", slotDragMove);
    document.addEventListener("pointerup", slotDragEnd);
    document.addEventListener("pointercancel", slotDragEnd);
  });

  /* Gemstone content is merchant-owned text from the Crystal metaobject.
     A native dialog provides top-layer positioning, focus containment and
     Escape dismissal without interfering with the stone picker sheet. */
  var benefitsDialog = root.querySelector("#trb-benefits");
  var benefitsIndex = null;
  function openBenefits(index) {
    var stone = picks[index];
    var info = stone && stone.benefits;
    if (!info || !info.description || !info.description.trim()) return;
    benefitsIndex = index;
    root.querySelector("#trb-benefits-title").textContent = info.name || stone.title;
    root.querySelector("#trb-benefits-description").textContent = info.description;
    var intentions = root.querySelector("#trb-benefits-intentions");
    intentions.textContent = "";
    (info.intentions || []).filter(Boolean).slice(0, 3).forEach(function (name) {
      var pill = document.createElement("span");
      pill.textContent = name;
      intentions.appendChild(pill);
    });
    benefitsDialog.showModal();
  }
  benefitsDialog.querySelector(".trb-benefits-close").addEventListener("click", function () { benefitsDialog.close(); });
  benefitsDialog.addEventListener("keydown", function (e) {
    // This text-only dialog has one focusable control. Keep Tab cycling on
    // it rather than letting focus leave for the browser toolbar.
    if (e.key !== "Tab") return;
    e.preventDefault();
    benefitsDialog.querySelector(".trb-benefits-close").focus();
  });
  benefitsDialog.addEventListener("click", function (e) {
    if (e.target !== benefitsDialog) return;
    var box = benefitsDialog.getBoundingClientRect();
    if (e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom) benefitsDialog.close();
  });
  benefitsDialog.addEventListener("close", function () {
    var opener = root.querySelector('[data-benefits="' + benefitsIndex + '"]');
    if (opener) opener.focus({ preventScroll: true });
    benefitsIndex = null;
  });

  /* ---------- events ---------- */
  root.querySelector("#trb-slots").addEventListener("click", function (e) {
    if (suppressSlotClick) { suppressSlotClick = false; return; }
    if (e.target.closest(".trb-drag")) return;
    var benefits = e.target.closest("[data-benefits]");
    if (benefits) { openBenefits(+benefits.dataset.benefits); return; }
    var rm = e.target.closest("[data-rm]");
    if (rm) { e.stopPropagation(); userTouchedStack = true; picks.splice(+rm.dataset.rm, 1); chosenFor = null; render(); return; }
    var slot = e.target.closest(".trb-slot");
    if (slot) openSheet(+slot.dataset.i);
  });
  root.querySelector("#trb-slots").addEventListener("keydown", function (e) {
    if ((e.key === "Enter" || e.key === " ") && e.target.classList.contains("trb-slot")) {
      e.preventDefault(); openSheet(+e.target.dataset.i);
    }
  });
  root.querySelector("#trb-grid").addEventListener("click", function (e) {
    var c = e.target.closest("[data-pick]"); if (!c) return;
    var s = byId(c.dataset.pick);
    userTouchedStack = true;
    if (slotIndex >= picks.length) picks.push(s); else picks[slotIndex] = s;
    chosenFor = null; // hand-picked, so it's no longer the curated stack
    closeSheet(); render();
  });
  root.querySelector("#trb-grid").addEventListener("mouseover", function (e) {
    var card = e.target.closest(".trb-card");
    if (!card || card.dataset.pick === hoverPreviewId) return;
    var s = byId(card.dataset.pick);
    if (!s) return;
    hoverPreviewId = card.dataset.pick;
    stagePreviewOn(s);
  });
  root.querySelector("#trb-grid").addEventListener("mouseout", function (e) {
    var toCard = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest(".trb-card");
    if (!toCard) stagePreviewOff();
  });
  root.querySelector("#trb-chips").addEventListener("click", function (e) {
    var c = e.target.closest("[data-f]"); if (!c) return;
    fIntent = c.dataset.f || null; renderFilters(); renderGrid();
    var sel = root.querySelector('#trb-chips .trb-chip[aria-pressed="true"]');
    if (sel) sel.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  });
  root.querySelector("#trb-swatches").addEventListener("click", function (e) {
    var c = e.target.closest("[data-c]"); if (!c) return;
    var v = c.dataset.c || null;
    fColour = (v && v === fColour) ? null : v;
    renderFilters(); renderGrid();
    var sel = root.querySelector('.trb-swatch[aria-pressed="true"]');
    if (sel) sel.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  });
  root.querySelector("#trb-clearfilters").addEventListener("click", function () {
    fIntent = null; fColour = null; renderFilters(); renderGrid();
  });
  root.querySelector("#trb-addmore").addEventListener("click", function () { openSheet(picks.length); });
  root.querySelector("#trb-close").addEventListener("click", closeSheet);
  root.querySelector("#trb-scrim").addEventListener("click", closeSheet);
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    if (benefitsDialog.open) return;
    // Escape while the gem badge popup is open should only close that popup.
    var gemPopup = document.getElementById("gem-collection-popup");
    if (gemPopup && gemPopup.open) return;
    closeSheet();
  });
  root.querySelector("#trb-clear").addEventListener("click", function () { userTouchedStack = true; picks = []; chosenFor = null; render(); });

  root.querySelector("#trb-shuffle").addEventListener("click", function () {
    if (!STONES.length) return;
    userTouchedStack = true;
    // Match however many bracelets are currently picked, never below MIN_REQUIRED.
    var n = Math.min(Math.max(picks.length, MIN_REQUIRED), MAXSLOTS);
    var out = buildStack(n);
    picks = out.stones;
    chosenFor = out.intention;
    render();
  });

  root.querySelector("#trb-sizes").addEventListener("click", function (e) {
    var b = e.target.closest("[data-i]"); if (!b) return;
    sizeIndex = +b.dataset.i;
    root.querySelectorAll(".trb-size").forEach(function (x) { x.setAttribute("aria-pressed", +x.dataset.i === sizeIndex); });
    renderSizeDetails(); renderSlots(); renderTotal();
  });

  root.querySelector("#trb-cta").addEventListener("click", addToCart);

  root.querySelectorAll(".trb-chips").forEach(function (el) {
    el.addEventListener("scroll", function () { updateFade(el); }, { passive: true });
  });

  // "Description / Details / Our Cause / Returns" accordion rendered below
  // the CTA (sections/bracelet-builder.liquid). The PDP's own click handler
  // (theme-product.js.liquid) is scoped to "#product-description", which
  // doesn't exist on this page, so it never binds here -- rebuilt scoped to
  // this section's own root instead. Animated via max-height (rather than a
  // plain display toggle) to match the PDP's slideUp/slideDown; scrollHeight
  // has to be read AFTER display is set to "block" since it reads as 0 on a
  // display:none element.
  function trbSlide(el, opening) {
    if (el._trbSlideCleanup) el._trbSlideCleanup();
    if (opening) el.style.display = "block";
    var target = el.scrollHeight;
    el.style.overflow = "hidden";
    el.style.maxHeight = (opening ? 0 : target) + "px";
    el.style.transition = "max-height .25s ease";
    requestAnimationFrame(function () {
      el.style.maxHeight = (opening ? target : 0) + "px";
    });
    var cleanup = function () {
      el.style.display = opening ? "block" : "none";
      el.style.maxHeight = ""; el.style.overflow = ""; el.style.transition = "";
      el.removeEventListener("transitionend", cleanup);
      el._trbSlideCleanup = null;
    };
    el._trbSlideCleanup = cleanup;
    el.addEventListener("transitionend", cleanup);
  }
  root.querySelectorAll(".rte .tabs .tab-title").forEach(function (title) {
    title.addEventListener("click", function () {
      var h2 = title.querySelector("h2");
      var wasCollapsed = h2.classList.contains("collapsed");
      root.querySelectorAll(".rte .tabs .tab").forEach(function (tab) {
        var otherH2 = tab.querySelector(".tab-title h2");
        if (!otherH2.classList.contains("collapsed")) {
          otherH2.classList.add("collapsed");
          trbSlide(tab.querySelector(".tab-content"), false);
        }
      });
      if (wasCollapsed) {
        h2.classList.remove("collapsed");
        trbSlide(title.parentElement.querySelector(".tab-content"), true);
      }
    });
  });

  // Page 1's worth of STONES is already available synchronously (rendered
  // by Liquid on first paint, same as a normal section), so boot happens
  // immediately -- no waiting on a fetch for at least the first 50.
  computeColorMath();

  // Arriving from a PDP's "Build your own set" button: ?prefill_id=<product.id>
  // fills slot 1 with that bracelet, then slots 2 and 3 are curated around it
  // the same way a shuffle would, so the product the customer was already
  // looking at anchors a real stack instead of landing alone.
  var prefillId = new URLSearchParams(window.location.search).get("prefill_id");
  var prefillStone = prefillId ? byId(prefillId) : null;
  // The named product can live on a background page (2+) that hasn't loaded
  // yet -- when more pages are coming, wait for fetchRemainingPages to
  // resolve it (see the "lateStone" handling below) rather than painting a
  // random stack first and swapping slot 1 out from under the customer.
  var awaitingPrefill = !!(prefillId && !prefillStone && TOTAL_PAGES > 1);

  // Open with a curated stack rather than an empty one, same algorithm as
  // the shuffle button, so first paint isn't just an empty stage -- unless
  // a prefill is still pending, in which case the empty stage IS first paint.
  if (prefillStone) {
    var initial = buildStack(Math.min(MIN_REQUIRED, MAXSLOTS), [prefillStone]);
    picks = initial.stones;
    chosenFor = initial.intention;
  } else if (!awaitingPrefill && STONES.length) {
    var initial = buildStack(Math.min(MIN_REQUIRED, MAXSLOTS));
    picks = initial.stones;
    chosenFor = initial.intention;
  }

  renderSizes();
  render();

  // Deferred so the initial curated stack paints first; requestIdleCallback
  // isn't in Safari, so fall back to a short timeout there.
  (window.requestIdleCallback || function (fn) { setTimeout(fn, 200); })(preloadStoneImages);

  // Only fires a fetch at all if the collection actually has more than 50
  // qualifying products (TOTAL_PAGES > 1) -- see fetchRemainingPages() above.
  fetchRemainingPages();

  /* The sticky stack's top offset needs to clear the site header, but the
     header is only fixed/sticky depending on section settings + template,
     and its height varies (announcement bar, etc.) -- measured, not
     hardcoded, so it stays correct if either of those changes. Cached in a
     plain variable (not re-read via getComputedStyle every scroll frame,
     which forces a style recalc and was part of what made scrolling feel
     shaky); only recomputed on load/resize. */
  var stageTopOffsetPx = 28;
  function updateStageTopOffset() {
    var header = document.querySelector(".scrollheader") || document.querySelector("header");
    var offset = 12;
    if (header) {
      var pos = window.getComputedStyle(header).position;
      if (pos === "fixed" || pos === "sticky") offset += header.getBoundingClientRect().height;
    }
    stageTopOffsetPx = offset;
    root.style.setProperty("--trb-stage-top", offset + "px");
  }
  updateStageTopOffset();

  /* Manual sticky, not CSS position:sticky: the theme's .shifter-page
     ancestor (shared with the off-canvas nav/cart drawer) sets
     overflow:hidden, which silently defeats native sticky.

     Back to position:fixed for the main tracking phase -- but committed
     ONCE, not rewritten every scroll frame. A constant "top" on a fixed
     element is then glued to the viewport by the browser's own compositor
     with zero further JS involvement, which is what makes it feel solid;
     recalculating "top" (or even transform) every frame adds work a plain
     native-feeling pin doesn't need.

     What must NOT come back is position:absolute;bottom:0 for parking:
     that requires reading the container's height every frame to decide the
     offset, and toggling flow-membership like that changed .trb-builder's
     own rendered height, which changed the document's scroll height, which
     shifted window.scrollY out from under the very calculation deciding
     whether to park -- a real feedback loop (confirmed live: scrollY
     oscillating on its own with no user input). So parking is instead a
     transform layered ON TOP of the already-fixed element -- it doesn't
     re-enter or re-leave flow, so it can update every frame safely. */
  var stageMQ = window.matchMedia("(min-width:940px)");
  var stagePinned = false;
  var stageLastPull = 0;

  function clearStagePin() {
    if (!stagePinned) return;
    stagePinned = false;
    stageLastPull = 0;
    var el = root.querySelector(".trb-stage");
    el.style.position = ""; el.style.top = ""; el.style.left = "";
    el.style.width = ""; el.style.transform = ""; el.style.willChange = "";
  }

  function updateStickyStage() {
    var stage = root.querySelector(".trb-stage");
    if (!stageMQ.matches) { clearStagePin(); return; }

    var offset = stageTopOffsetPx;
    var cRect = root.getBoundingClientRect();
    var stageHeight = stage.offsetHeight;
    var maxTranslate = cRect.height - stageHeight;

    if (cRect.top >= offset || maxTranslate <= 0) {
      clearStagePin();
      return;
    }

    if (!stagePinned) {
      stagePinned = true;
      stageLastPull = -1; // force the first pull write below, even if it's 0
      var lockedWidth = stage.getBoundingClientRect().width;
      stage.style.willChange = "transform";
      stage.style.position = "fixed";
      stage.style.top = offset + "px";
      stage.style.left = cRect.left + "px";
      stage.style.width = lockedWidth + "px";
    }

    // Once tracking at a constant top:offset would push the stage past the
    // container's bottom, pull it back up by exactly that much via
    // transform. Only writes the style when the value actually changes, so
    // most of the tracking phase (pull staying at 0) touches nothing.
    var pull = Math.max(0, (offset - cRect.top) - maxTranslate);
    if (pull !== stageLastPull) {
      stage.style.transform = pull ? "translateY(-" + Math.round(pull) + "px)" : "";
      stageLastPull = pull;
    }
  }

  var stageScrollTicking = false;
  window.addEventListener("scroll", function () {
    if (stageScrollTicking) return;
    stageScrollTicking = true;
    requestAnimationFrame(function () { updateStickyStage(); stageScrollTicking = false; });
  }, { passive: true });
  updateStickyStage();

  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      renderStack(); updateFades(); updateStageTopOffset();
      clearStagePin(); updateStickyStage();
    }, 120);
  });
})();
